import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	MatTableDataSource,
	MatTable,
	MatRecycleRows,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { CompanyEntityRelationship } from 'src/app/models/data/entityRelationship';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import DeviceAlert = Components.Schemas.DeviceAlert;
import DeviceAlertType = Components.Schemas.DeviceAlertType;
import { UserProfile } from 'src/app/models/model';
import { MatDivider } from '@angular/material/list';
import { NgClass, DatePipe, KeyValuePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DataSetTableMenuComponent } from '../../../components/data-set/data-set-table.component';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { ShortNamedOffsetPipe } from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

export type MenuItem = {
	type: string;
	serialNumber: string;
	propertyName: string;
	location: string;
	timestamp: string;
};

@Component({
	selector: 'app-device-alerts-main-tab',
	templateUrl: './device-alerts-main-tab.component.html',
	styleUrls: ['./device-alerts-main-tab.scss', '../../common/main.scss'],
	imports: [
		MatDivider,
		NgClass,
		MatIcon,
		DataSetComponent_1,
		DataSetContentComponent,
		MatTable,
		MatRecycleRows,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		MatSortHeader,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		DatePipe,
		KeyValuePipe,
		ShortNamedOffsetPipe,
		DataSetLoadingRowComponent,
	],
})
export class DeviceAlertsComponent implements AfterViewInit, OnDestroy, OnInit {
	@Input() dataRequest!: (
		queryParams: QueryParam[]
	) => Promise<CompanyEntityRelationship[]>;

	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild(MatSort) sort = new MatSort();
	@ViewChild(CdkVirtualScrollViewport) virtualScroll!: CdkVirtualScrollViewport;

	displayedColumns: string[] = [
		'deviceAlertType',
		'serialNumber',
		'propertyName',
		'location',
		'utcTriggerTime',
		'rowMenu',
	];

	MISSING_KEY: DeviceAlertType = 'missingKey';
	LOW_BATTERY: DeviceAlertType = 'lowBattery';
	KEY_NOT_DETECTED: DeviceAlertType = 'keyNotDetected';

	dataSource = new MatTableDataSource<DeviceAlert>();
	deviceAlertDataSource = new MatTableDataSource<DeviceAlert>();
	dataSourceScrolling = new MatTableDataSource<DeviceAlert>();

	loadingTable = false;
	isOracle = false;

	isViewAll = true;
	isViewLess = false;
	hasMoreData = true;

	// increased this so the it tooks like the filtering on the screen is working correctly
	// Harvey is going to revamp the dashboard header so it is clear to the end users that area is clickable
	numRowDisplay = 100;

	start = 0;
	end: number = this.numRowDisplay + this.start;

	numMissingKey = '0';
	numLowBattery = '0';
	numKeyNotDetected = '0';
	showKeyDetection = false;

	map = new Map<string, number>();
	viewAllButton: any;

	private filterSubscription?: Subscription;
	compiledFilters: QueryParam[] = [];
	filters: QueryParam[] = [];
	id = '';
	data = new Array<DeviceAlert>();
	private observer: IntersectionObserver | undefined;
	DeviceAlertTotal: Components.Schemas.DeviceAlertTotal[] | undefined;

	selectedDeviceAlertType?: string;

	getDeviceAlertsLoadingKey = ApiLoadingKeys.getDeviceAlerts;
	finishedLoadingInitData: boolean = false;
	enableSenturianFF: boolean = false;

	constructor(
		private router: Router,
		public readonly loadingService: LoadingIndicatorService,
		private readonly apiService: ApiService,
		readonly timeZoneService: TimeZoneService
	) {
		this.compiledFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.compiledFilters;
	}

	async ngOnInit(): Promise<void> {
		this.enableSenturianFF = (
			await this.apiService.getFeatureFlagByName(
				FeatureFlagName.enableSenturian
			)
		).value;
		this.resetParameters();
	}

	async ngAfterViewInit(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		const company = userProfile?.activeRoleDetails?.company;
		// only show if feature switch is ON and company setting is turned ON
		this.showKeyDetection = company?.keyDetection ?? false;

		this.dataSet.applyFilters();

		this.viewAllButton = document.getElementById('viewAllButton');

		this.getTableData(0);

		if (this.viewAllButton && this.hasMoreData) {
			this.observer?.observe(this.viewAllButton);
		}
	}

	layoutData(queryParameters: QueryParam[], isInitialLayout: boolean): void {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async () => {
					const result = await this.apiService.getDeviceAlerts(queryParameters);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((response) => {
				// DE4963: we already have all this data available on the client-side once we
				// get the data from getDeviceAlerts(), so we do not need a separate call specifically for the numbers.
				const alertTypeLowBattery = this.filterAlertUniqueDeviceIds(
					response,
					this.LOW_BATTERY
				);
				const alertTypeMissingKey = this.filterAlertUniqueDeviceIds(
					response,
					this.MISSING_KEY
				);
				const alertTypeKeyNotDetected = this.filterAlertUniqueDeviceIds(
					response,
					this.KEY_NOT_DETECTED
				);
				this.numLowBattery = alertTypeLowBattery.length.toString();
				this.numMissingKey = alertTypeMissingKey.length.toString();
				this.numKeyNotDetected = alertTypeKeyNotDetected.length.toString();

				if (response.length === 0) {
					this.hasMoreData = false;
				}
				this.dataSource = new MatTableDataSource<DeviceAlert>(response);
				this.data = this.dataSource.data;
				if (this.hasMoreData) {
					this.data.forEach((deviceAlert) => {
						// calculate number of alerts of each type based on LB serial number
						// and them to a hashmap
						const key = deviceAlert.serialNumber + deviceAlert.deviceAlertType;

						const val = this.map.get(key);
						this.map.set(key, (val ?? 0) + 1);

						if (!val) {
							// filter out the rows only to display if they either different LB Serial Number or Alert type
							// if a LB have same type of alert more than once, they need to be club together, and a ticker is displayed
							this.deviceAlertDataSource.data.push(deviceAlert);
						}
					});
					this.dataSourceScrolling.data = this.deviceAlertDataSource.data;
				}
				this.dataSourceScrolling.sort = this.sort;
				this.start = this.numRowDisplay + this.start;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
		if (this.observer) {
			this.observer.disconnect();
			this.observer = undefined;
		}
	}

	// Make sure the alerts are distinct per alert type and device id
	filterAlertUniqueDeviceIds(
		alerts: Components.Schemas.DeviceAlert[],
		deviceAlertType: DeviceAlertType
	) {
		const uniqueIds: string[] = [];

		return alerts.filter((alert) => {
			if (alert.deviceAlertType !== deviceAlertType) {
				return false;
			}
			const isDuplicate = uniqueIds.includes(alert.deviceId);
			if (!isDuplicate) {
				uniqueIds.push(alert.deviceId);
				return true;
			}
			return false;
		});
	}

	onViewAllClicked(): void {
		this.getTableData(this.start);
	}

	onViewLessClicked(): void {
		const queryParameters: QueryParam[] = [
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: this.numRowDisplay },
		];

		// Reset all parameters before making api call again
		this.resetParameters();
		this.layoutData(queryParameters, true);
	}

	getTableData(start: number): void {
		// we aren't doing intersection of data since we are returing 100 rows at a time so removed the code
		// the dashboard needs reworked on how to handle pagination

		const queryParameters: QueryParam[] = [
			{ param: 'rowIndex', value: this.start },
			{ param: 'rowCount', value: this.numRowDisplay },
			// pass this in to make sure keyDetection alerts are not returned if the feaure is turned off or off a company level
			{
				param: 'deviceAlertType',
				value: this.showKeyDetection
					? [this.LOW_BATTERY, this.MISSING_KEY, this.KEY_NOT_DETECTED]
					: [this.LOW_BATTERY, this.MISSING_KEY],
			},
		];

		this.layoutData(queryParameters, false);
	}

	// filter records based on alert type
	filterDeviceAlertData(deviceAlertType: string): void {
		this.dataSourceScrolling = new MatTableDataSource<DeviceAlert>();
		for (const columnName of this.deviceAlertDataSource.data) {
			if (columnName.deviceAlertType === deviceAlertType) {
				this.dataSourceScrolling.data.push(columnName);
			}
		}
	}

	onTabClicked(deviceAlertType: string): void {
		this.selectedDeviceAlertType = deviceAlertType;
		this.filterDeviceAlertData(deviceAlertType);
	}

	onRowClick(element: any): void {
		this.router.navigate(['/devices', element.deviceId]);
	}

	resetParameters(): void {
		this.numLowBattery = '0';
		this.numMissingKey = '0';
		this.numKeyNotDetected = '0';
		this.map = new Map<string, number>();
		this.dataSourceScrolling = new MatTableDataSource<DeviceAlert>();
		this.deviceAlertDataSource = new MatTableDataSource<DeviceAlert>();
		this.start = 0;
		this.end = this.numRowDisplay + this.start;
		this.hasMoreData = true;
	}

	async dismissAlert(deviceAlert: DeviceAlert): Promise<void> {
		await this.apiService
			.dismissAlert(deviceAlert.id, deviceAlert)
			.then((res) => {
				if (!!res) {
					// reset the parameters
					this.resetParameters();

					// call this to get new table data. Calling layoutData was messing up the instances of device alert.
					this.getTableData(0);
				}
			});
	}

	areThereAnyAlerts() {
		return (
			Number(this.numMissingKey) !== 0 ||
			Number(this.numLowBattery) !== 0 ||
			Number(this.numKeyNotDetected) !== 0
		);
	}

	areThereAnyMissingKeyAlerts() {
		return Number(this.numMissingKey) !== 0;
	}

	areThereAnyLowBatteryAlerts() {
		return Number(this.numLowBattery) !== 0;
	}

	areThereAnyKeyNotDetectedAlerts() {
		return Number(this.numKeyNotDetected) !== 0;
	}

	isLoading() {
		return (
			this.loadingService.isLoading(this.getDeviceAlertsLoadingKey) ||
			!this.finishedLoadingInitData
		);
	}

	getApiService() {
		return this.apiService;
	}

	rowId(row: DeviceAlert): string {
		return row?.id || 'unknown-id';
	}
}
